import * as React from "react";
import { useCustomerSession } from "../customer-session-provider";

export interface CustomerCartIdConsumerInterface {
  cartId?: string;
}
const defaultValue = {} as unknown as CustomerCartIdConsumerInterface;
const CustomerCartIdContext = React.createContext(defaultValue);

export interface CustomerCartId_ControllerConsumerInterface {
  handleNewCustomerCartId: (cartId?: string) => void;
}
const defaultValue_controller =
  {} as unknown as CustomerCartId_ControllerConsumerInterface;
const CustomerCartId_ControllerContext = React.createContext(
  defaultValue_controller
);

export interface InitialStateMinimumRequirements_CustomerCartId {
  cartId?: string;
}

export interface CustomerCartIdProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_CustomerCartId;
  };
}
export const CustomerCartIdProvider: React.FunctionComponent<
  CustomerCartIdProviderProps
> = (props) => {
  const { isCustomerLoggedIn } = useCustomerSession();

  const { initialState } = props.useInitialState();

  const [lastCustomerLoggedInState, setLastCustomerLoggedInState] =
    React.useState(() => isCustomerLoggedIn);

  const [cartId, setCartId] = React.useState(() => {
    const { cartId: initialCartId } = initialState || {};
    return initialCartId;
  });

  React.useEffect(() => {
    if (isCustomerLoggedIn !== lastCustomerLoggedInState) {
      setLastCustomerLoggedInState(isCustomerLoggedIn);
      setCartId(undefined);
    }
  }, [isCustomerLoggedIn, lastCustomerLoggedInState]);

  const handleNewCustomerCartId: CustomerCartId_ControllerConsumerInterface["handleNewCustomerCartId"] =
    React.useCallback(
      (cartId) => {
        setCartId(cartId);
      },
      [setCartId]
    );

  const value: CustomerCartIdConsumerInterface = React.useMemo(
    () => ({
      cartId,
    }),
    [cartId]
  );

  const controllerValue: CustomerCartId_ControllerConsumerInterface =
    React.useMemo(
      () => ({
        handleNewCustomerCartId,
      }),
      [handleNewCustomerCartId]
    );

  return (
    <CustomerCartIdContext.Provider value={value}>
      <CustomerCartId_ControllerContext.Provider value={controllerValue}>
        {props.children || []}
      </CustomerCartId_ControllerContext.Provider>
    </CustomerCartIdContext.Provider>
  );
};

export const useCustomerCartId = () => {
  const value = React.useContext(CustomerCartIdContext);
  if (value === defaultValue) {
    throw new Error(
      "useCustomerCartId must be used within a CustomerCartIdProvider"
    );
  }
  return value;
};

export const useCustomerCartId_asController = () => {
  const value = React.useContext(CustomerCartId_ControllerContext);
  if (value === defaultValue_controller) {
    throw new Error(
      "useCustomerCartId_asController must be used within a CustomerCartIdProvider"
    );
  }
  return value;
};
